import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Check } from 'lucide-react';

const MORNING_THREAD_KEY = 'wwi_morning_thread';
const CALENDAR_KEY = 'wwi_daily_calendar_task';
const ONBOARDING_KEY = 'wwi_onboarding_complete';

const getTodayKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
};

export const useMorningThread = (enabled = true) => {
  const [showCard, setShowCard] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [smartWindowActive, setSmartWindowActive] = useState(false);

  // Check if calendar card was dismissed today to show morning thread
  useEffect(() => {
    if (!enabled) { setShowCard(false); return; }

    const check = () => {
      const todayKey = getTodayKey();

      // Don't show during onboarding - only one prompt at a time
      const onboardingComplete = localStorage.getItem(ONBOARDING_KEY) === 'true';
      if (!onboardingComplete) {
        setShowCard(false);
        return;
      }

      // Check if calendar was completed today
      const calStored = localStorage.getItem(CALENDAR_KEY);
      if (!calStored) return;
      try {
        const calData = JSON.parse(calStored);
        if (calData.date !== todayKey || !calData.completed) return;
      } catch { return; }

      // Check morning thread status
      const stored = localStorage.getItem(MORNING_THREAD_KEY);
      if (stored) {
        try {
          const data = JSON.parse(stored);
          if (data.date !== todayKey) {
            setShowCard(true);
            setIsCompleted(false);
          } else {
            setShowCard(!data.completed);
            setIsCompleted(data.completed);
          }
        } catch {
          setShowCard(true);
          setIsCompleted(false);
        }
      } else {
        setShowCard(true);
        setIsCompleted(false);
      }
    };

    check();
    // Re-check periodically in case calendar card gets dismissed
    const interval = setInterval(check, 500);
    return () => clearInterval(interval);
  }, [enabled]);

  const dismissCard = useCallback(() => {
    const todayKey = getTodayKey();
    localStorage.setItem(MORNING_THREAD_KEY, JSON.stringify({
      date: todayKey,
      completed: true
    }));
    setIsCompleted(true);
    setSmartWindowActive(true);
    setTimeout(() => setShowCard(false), 300);
    // 20s smart window — any task created will become current thread
    setTimeout(() => setSmartWindowActive(false), 20000);
  }, []);

  return { showCard, isCompleted, dismissCard, smartWindowActive };
};

export const MorningThread = ({ enabled = true }) => {
  const { showCard, isCompleted, dismissCard } = useMorningThread(enabled);

  if (!enabled || !showCard) return null;

  return (
    <AnimatePresence>
      {showCard && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: isCompleted ? 0 : 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="mb-3"
          data-testid="morning-thread-card"
        >
          <div className="bg-[#F0FAFA] border border-[#E0F2F2] rounded-xl px-3 py-2 shadow-sm" style={{ transform: 'scaleX(1) scaleY(0.95)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-light text-[#78909C] uppercase tracking-wider opacity-80">
                Daily
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={dismissCard}
                className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-[#3DC4D0] hover:bg-[#3DC4D0] hover:border-[#3DC4D0] transition-colors group flex items-center justify-center"
                data-testid="morning-thread-dismiss"
              >
                <Check 
                  size={12} 
                  className="text-transparent group-hover:text-white transition-colors" 
                />
              </button>
              
              <Compass size={16} className="text-[#78909C] flex-shrink-0" />
              
              <p className="text-sm text-[#52525B] font-light">
                What do you want to move forward today?
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MorningThread;
